import type { Props as FileUploadProps } from '@service-victoria/component-library/components/FileUpload';
import { FileMode } from '@service-victoria/component-library/components/FileUpload/props';
import { Step as StepProps } from '@service-victoria/component-library/components/Wizard/props';
import { FormikErrors, FormikTouched } from 'formik';
import { lazy } from 'react';
import * as Yup from 'yup';

import * as Interfaces from 'app/interfaces';
import { validateOrRedeemBulkVouchers } from 'app/services/gakService';
import * as Steps from '../..';
import { constants } from 'app/utils';

import type { FormValues } from './interfaces';
import { FileMapping, Redeem } from 'app/interfaces/common';

const initialValues: FormValues = {
  redeem: {
    bulk: { fileUpload: [], fileMapping: [] }
  }
};

const initialErrors: FormikErrors<FormValues> = {};
const initialTouched: FormikTouched<FormValues> = {};

export const bulkSchema = Yup.object<Redeem['bulk']>().shape({
  fileUpload: Yup.array<FileUploadProps.FileMeta[]>()
    .default(initialValues.redeem.bulk.fileUpload)
    .test('checkAtLeastOneUpload', 'You must upload a CSV file before continuing', files => {
      return (
        files &&
        files.length > 0 &&
        files.every(file => [FileMode.ADDED, FileMode.FINISHED].includes(file.state))
      );
    }),
  fileMapping: Yup.array<FileMapping>().default(initialValues.redeem.bulk.fileMapping)
});

const redeemSchema = Yup.object<FormValues['redeem']>().shape({
  bulk: bulkSchema
});

const validationSchema = Yup.object<FormValues>().shape({
  redeem: redeemSchema
});

const Component = lazy(() => import('./RedeemBulk'));

const Step: StepProps<FormValues> = {
  id: Interfaces.Common.RedeemStep.Bulk,
  title: constants.RedemptionPage.title,
  stepName: 'Redeem voucher',
  Component,
  validation: {
    schema: validationSchema,
    fieldNames: ['redeem.bulk.fileUpload', 'redeem.bulk.fileMapping']
  },
  navigation: {
    normal: [
      {
        content: 'Back',
        type: 'secondary',
        shouldValidate: false,
        onClick: () => {
          window.location.href = `${import.meta.env.BASE_URL}${constants.Routes.Dashboard}` || '/';
        }
      },
      {
        content: 'Bulk redeem',
        shouldValidate: true,
        type: 'primary',
        onClick: async (context, formProps, appStates): Promise<void> => {
          context.dispatch({
            type: 'API_REQUEST',
            status: 'pending'
          });

          const validateOnly = true;
          const results = await validateOrRedeemBulkVouchers(
            formProps.values.redeem.bulk,
            formProps,
            appStates,
            validateOnly
          );

          if (results) {
            const formValues = {
              ...formProps.values,
              redeem: {
                ...formProps.values.redeem,
                bulk: {
                  ...formProps.values.redeem.bulk,
                  aggregateData: results
                }
              }
            };
            context.dispatch({
              type: 'GOTO_STEP',
              stepId: Steps.Review.Step.id,
              values: formValues
            });
          } else {
            // Reset form (except for the error status) if the bulk redemption endpoint rejects the file
            // This is to make it simpler to upload a new file - having to remove the file in error before uploading the replacement file may cause confusion
            formProps.setFieldValue('redeem.bulk.fileUpload', []);
            formProps.setFieldValue('redeem.bulk.fileMapping', []);
            formProps.setFieldTouched('redeem.bulk.fileUpload', false, false);
          }

          // stop spinner
          context.dispatch({
            type: 'API_REQUEST',
            status: 'resolved'
          });
        }
      }
    ]
  }
};

export { Step, initialErrors, initialTouched, initialValues, redeemSchema, validationSchema };
